import { ThemeOptions, createTheme } from '@mui/material/styles';

import React from 'react';

export const colors = {
    bgGray: 'rgba(31, 33, 35, 0.60)',
    black: '#1F2123',
    charcoal: '#272D32',
    coolGray: '#CDD0D3',
    darkBlue: '#2D4F5F',
    deepGray: '#7A818B',
    disabledGray: 'rgba(255, 255, 255, 0.3)',
    disabledWhite: 'rgba(255, 255, 255, 0.60)',
    dividerBlueGray: '#1D4448',
    dividerGray: '#4F535A',
    errorRed: '#FF6B6B',
    hintText: 'rgba(44, 47, 50, 0.65)',
    hoverWhite: 'rgba(255, 255, 255, 0.08)',
    lightBlack: 'rgba(0, 0, 0, 0.60)',
    lightGray: '#CCCCCC',
    mediumGray: '#BEBEBE',
    selectedWhite: 'rgba(255, 255, 255, 0.16)',
    semiTransparentWhite: 'rgba(255, 255, 255, 0.75)',
    tealBlue: '#73C9D8',
    white: '#FFFFFF',
    yellow: '#EBD529',
};

// Extend the theme to add the 'hint' property to the `text` palette
declare module '@mui/material/styles' {
    interface TypeText {
        hint?: string;
        black?: string;
    }

    interface TypeOther {
        darkBlue?: string;
        lightBlack?: string;
    }

    interface PaletteOptions {
        text?: Partial<TypeText>;
        other?: Partial<TypeOther>;
    }
}

// Extend the theme to add the 'hint' property to the `background` palette
declare module '@mui/material/styles' {
    interface TypeBackground {
        bgGray?: string;
    }

    interface PaletteOptions {
        background?: Partial<TypeBackground>;
    }
}

declare module '@mui/material/styles' {
    interface TypographyVariants {
        h1_Thin: React.CSSProperties;
        h3_Thin: React.CSSProperties;
        h4_Thin: React.CSSProperties;
        h6_Thin: React.CSSProperties;
        h7: React.CSSProperties;
        h8: React.CSSProperties;
        body1_Thin: React.CSSProperties;
        body1_Bold: React.CSSProperties;
        body2_Thin: React.CSSProperties;
        caption1: React.CSSProperties;
        caption1_Thin: React.CSSProperties;
        caption2: React.CSSProperties;
        caption2_Thin: React.CSSProperties;
        finePrint: React.CSSProperties;
    }

    // allow configuration using `createTheme()`
    interface TypographyVariantsOptions {
        h1_Thin?: React.CSSProperties;
        h2_Thin?: React.CSSProperties;
        h3_Thin?: React.CSSProperties;
        h4_Thin?: React.CSSProperties;
        h6_Thin?: React.CSSProperties;
        h7: React.CSSProperties;
        h8: React.CSSProperties;
        body1_Thin?: React.CSSProperties;
        body1_Bold?: React.CSSProperties;
        body2_Thin?: React.CSSProperties;
        caption1?: React.CSSProperties;
        caption1_Thin?: React.CSSProperties;
        caption2?: React.CSSProperties;
        caption2_Thin?: React.CSSProperties;
        finePrint?: React.CSSProperties;
    }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        h1_Thin: true;
        h4_Thin: true;
        h6_Thin: true;
        h2_Thin: true;
        h3_Thin: true;
        h7: true;
        h8: true;
        body1_Thin: true;
        body1_Bold: true;
        body2_Thin: true;
        caption1: true;
        caption1_Thin: true;
        caption2: true;
        caption2_Thin: true;
        finePrint: true;
    }
}
declare module '@mui/system' {
    interface BreakpointOverrides {
        xxl: true; // Add xxl as a new breakpoint
    }
}

const themeOptions: ThemeOptions = {
    breakpoints: {
        values: {
            lg: 1920, // Large screens start from 1920px (optional)
            md: 1300, // Medium screens start from 1300px
            sm: 700, // Small screens start from 900px
            xl: 2560, // Extra large screens
            xs: 0,
            xxl: 3840,
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    textTransform: 'none',
                },
            },
        },
    },
    palette: {
        action: {
            active: colors.mediumGray, // Medium gray for active states
            disabled: colors.disabledGray, // Disabled state
            hover: colors.hoverWhite, // Hover effects
            selected: colors.selectedWhite, // Selected item background
        },
        background: {
            bgGray: colors.bgGray,
            default: colors.black, // Black/Dark grayish black
            paper: colors.charcoal, // Charcoal for containers
        },
        divider: colors.dividerGray, // Darker gray for dividers
        error: {
            main: colors.errorRed, // Error color
        },
        grey: {
            100: colors.lightGray, // Light gray
            200: colors.mediumGray, // Medium gray
            300: colors.coolGray, // Cool gray
            400: colors.deepGray, // Deep gray
            500: colors.dividerGray, // Darker gray
            600: colors.dividerBlueGray, // blue gray
        },
        info: {
            main: colors.semiTransparentWhite,
        },
        other: {
            darkBlue: colors.darkBlue, // Dark blue for special cases
            lightBlack: colors.lightBlack, // Light black for special cases
        },
        primary: {
            contrastText: colors.black, // Dark contrast text
            main: colors.yellow, // Yellow accent
        },
        secondary: {
            contrastText: colors.white, // White contrast text
            main: colors.tealBlue, // Teal blue
        },
        text: {
            black: colors.black, // Black for main text
            disabled: colors.disabledWhite, // Disabled text
            hint: colors.hintText, // Hint text
            primary: colors.white, // White for main text
            secondary: colors.semiTransparentWhite, // Semi-transparent white for subtitles
        },
    },
};

const theme = createTheme(themeOptions);
theme.typography = {
    ...theme.typography,
    body1: {
        fontFamily: 'Noto Sans, Libre Franklin, sans-serif',
        fontSize: '1.5rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '3rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.625rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
    body1_Bold: {
        fontFamily: 'Space Grotesk, Libre Franklin, sans-serif',
        fontSize: '1.5rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '2.5rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.625rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
    body1_Thin: {
        fontFamily: 'Space Grotesk, Libre Franklin, sans-serif',
        fontSize: '1.5rem',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.625rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
    body2: {
        fontFamily: 'Noto Sans, Libre Franklin, sans-serif',
        fontSize: '1.25rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '2.5rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.875rem' },
    },
    body2_Thin: {
        fontFamily: 'Space Grotesk, Libre Franklin, sans-serif',
        fontSize: '1.25rem',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '3rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.875rem' },
    },
    caption1: {
        fontFamily: 'Space Grotesk, Libre Franklin, sans-serif',
        fontSize: '1.125rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '2rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.75rem' },
    },
    caption1_Thin: {
        fontFamily: 'Space Grotesk, Libre Franklin, sans-serif',
        fontSize: '1.125rem',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '2.5rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.75rem' },
    },
    caption2: {
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '0.9375rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '1.25rem',
        [theme.breakpoints.down('xl')]: { fontSize: '1rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.8125rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.75rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.6875rem' },
    },
    caption2_Thin: {
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '0.9375rem',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '1.25rem',
        [theme.breakpoints.up('xxl')]: { fontSize: '2rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.8125rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.75rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.6875rem' },
    },
    finePrint: {
        fontFamily: 'Space Grotesk, Libre Franklin, sans-serif',
        fontSize: '0.875rem',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '1.85rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.75rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.6875rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.625rem' },
    },
    fontFamily: 'Libre Franklin, Space Grotesk, Karla, Anonymous Pro, Arial, sans-serif',
    h1: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '4.5rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '12rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '5rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '4.5rem' },
        [theme.breakpoints.down('md')]: { fontSize: '3.75rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '3rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '2.5rem' },
    },
    h1_Thin: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '4rem',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '10rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '4.5rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '4rem' },
        [theme.breakpoints.down('md')]: { fontSize: '3.5rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '2.75rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '2.25rem' },
    },
    h2: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '3.5rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '5.375rem',
        [theme.breakpoints.up('xxl')]: { fontSize: '8rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '4rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '3.5rem' },
        [theme.breakpoints.down('md')]: { fontSize: '3rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '2.5rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '2rem' },
    },
    h3: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '1.625rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '4.5rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.75rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.625rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1.25rem' },
    },
    h3_Thin: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '1.625rem',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '3rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.75rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.625rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1.25rem' },
    },
    h4: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '1.25rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.1875rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
    h4_Thin: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '1.25rem',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '3.5rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.1875rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
    h5: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '1.125rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '2.5rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.0625rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.875rem' },
    },
    h6: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '2.5rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.8125rem' },
    },
    h6_Thin: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '2rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.8125rem' },
    },
    h7: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '0.9375rem',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '1.25rem',
        [theme.breakpoints.up('xxl')]: { fontSize: '2rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.8125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.75rem' },
    },
    h8: {
        fontFamily: 'Libre Franklin, sans-serif',
        fontSize: '0.875rem',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.8125rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.75rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.6875rem' },
    },
    subtitle1: {
        fontFamily: 'Karla, sans-serif',
        fontSize: '2rem',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '5rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '2.25rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '2rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.875rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.75rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1.5rem' },
    },
    subtitle2: {
        fontFamily: 'Karla, sans-serif',
        fontSize: '1.375rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.up('xxl')]: { fontSize: '2rem' },
        [theme.breakpoints.down('xl')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
};
export default theme;
